import { convertHelpers, getGoogleSheetDataValues } from 'global/common/convert';
import React, { useCallback, useContext, useState } from 'react'
import { ParentContext } from '../ParentContext';
import { createRefPath_event } from 'global/firestoreData/appData/appRefPaths';
import { logBatch } from 'global/cnr/reducers/reducerHelpers/logging';
import { writeBatch } from 'firebase/firestore';
import { dataFix } from 'global/common/dataFix';
import { getNow } from 'global/common/dateFormatting';
import { _cacheGroupTypes } from './useTimestamp';
import { getHeaders } from 'global/components/headers/getHeaders';

export const useAppData = () => {

  const parentContext = useContext(ParentContext);
  const { states, fns, fn_cls } = parentContext ?? {}
  const { app_state, eventInfo_state, paps_state, currentSettings_state } = states ?? {}
  const { database_fns } = fns
  const { mainDb } = app_state ?? {}
  const { appDataDocuments } = eventInfo_state ?? {}
  const { pathViews } = paps_state ?? {}
  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
  const { googleSheets } = aps_global ?? {}
  const { googleSheetsId } = googleSheets ?? {}

  const [updating, setUpdating] = useState()

  const exportAllToGoogleSheets = useCallback(async (callback) => {
    setUpdating(true)
    handleExportAllToGoogleSheets(fn_cls, googleSheetsId, appDataDocuments)
    callback()
    setUpdating(false)
  }, []);

  const exportToGoogleSheets = useCallback(async (appDataDocuments, addDataKey, callback) => {
    setUpdating(true)
    const { values, tabName } = getGoogleSheetsData(appDataDocuments, addDataKey)
    await fn_cls.updateGoogleSheetValues(googleSheetsId, tabName, values)
    callback()
    setUpdating(false)
  }, []);

  const restoreFromBackup = useCallback(async (selectedInfo, backupData, callback) => {
    await handleRestoreFromBackupData(mainDb, pathViews, selectedInfo, backupData, pathViews, callback)
  }, []);

  const removeBadData = useCallback(async (selectedInfo, callback) => {
    await handleRemoveBadData(database_fns, selectedInfo, pathViews, callback)
  }, []);

  const backupAllDocuments = useCallback(async (callback) => {
    await handleBackupAllDocuments(mainDb, database_fns, appDataDocuments, pathViews, callback)
  }, []);

  const backupDocuments = useCallback(async (callback) => {
    await handleBackups(mainDb, database_fns, appDataDocuments, pathViews, callback)
  }, []);

  const backupDocument = useCallback(async (callback) => {
    await handleBackup(mainDb, database_fns, pathViews, callback)
  }, []);

  const updateDocuments = useCallback(async (collectionKey, collectionData, callback) => {
    setUpdating(true)
    await handleUpdateDocuments(mainDb, database_fns, collectionKey, collectionData, pathViews, callback)
  }, []);

  const updateMultipleDocuments = useCallback(async (collectionKeys, callback) => {
    setUpdating(true)
    await handleUpdateMultipleDocuments(mainDb, database_fns, collectionKeys, pathViews, callback)
  }, []);

  const getBackup = useCallback(async (selectedInfo, callback) => {
    await handleGetBackup(database_fns, pathViews, selectedInfo, callback)
  }, []);

  return {
    exportAllToGoogleSheets,
    exportToGoogleSheets,
    restoreFromBackup,
    removeBadData,
    backupAllDocuments,
    backupDocuments,
    backupDocument,
    updateDocuments,
    updateMultipleDocuments,
    getBackup,
    updating
  }
}

const handleGetBackup = async (database_fns, pathViews, selectedInfo, callback) => {
  const { appDataItemKey } = selectedInfo
  const refPath = createRefPath_event(pathViews, [appDataItemKey], true);
  database_fns.get_data({ refPath, callback: callback })
}

const handleExportAllToGoogleSheets = async (fn_cls, googleSheetsId, appDataDocuments) => {
  // Create an array to hold all the promises
  const promises = [];

  // Use forEach to loop through _appDataDocuments
  _.forEach(appDataDocuments, (appDataDocument, appDataKey) => {
    const { values, tabName } = getGoogleSheetsData(appDataDocument, appDataKey);
    // Push the async call to the promises array
    const promise = fn_cls.updateGoogleSheetValues(googleSheetsId, tabName, values)
      .then(() => {
        console.log('addKey', appDataKey, tabName, values);
      });
    promises.push(promise);
  });

  // Wait for all promises to resolve
  await Promise.all(promises);
}

const getSelectedInfo = (key, selectedInfo) => {
  const selected_info = { ...selectedInfo };
  const isSameSelection = key === selected_info.appDataItemKey;

  // Toggle selection state
  selected_info.appDataItemKey = isSameSelection ? null : key;

  // Update app data and view item based on new selection
  const selectedItemKey = selected_info.appDataItemKey;
  selected_info.appData = selectedItemKey ? _.cloneDeep(currentResults[selectedItemKey]) : [];
  selected_info.viewItem = aps_viewItems ? aps_viewItems[selectedItemKey] : {};
  selected_info.appDataItem = null;
  selected_info.appDataItemItem = null;

  // Extract unique properties and order app data
  const vi = aps_viewItems[selectedItemKey];
  const { dataConnection } = vi ?? {};
  const { uniqueProps } = dataConnection ?? {};

  const _selectedAppData = _.orderBy(
    selected_info.appData,
    [
      (item) => item?.selected,
      (item) => (item?.lastName || "").toLowerCase(),
      (item) => (item?.name || "").toLowerCase()
    ]
  );

  // Generate item keys and update state
  convertHelpers.createItemKeys(selected_info.appData);
  return { selected_info, _selectedAppData, uniqueProps, isSameSelection }
}

const getGoogleSheetsData = (appDataDocuments, appDataKey) => {
  const appDataDocument = appDataDocuments?.[appDataKey]
  const { headers } = getHeaders(appDataDocument)
  const { values, tabName } = getGoogleSheetDataValues(_.values(appDataDocument), headers, _.startCase(appDataKey), null, null, null, true, true, appDataDocuments)
  return { values, tabName }
}

const handleRestoreFromBackupData = async (mainDb, pathViews, selectedInfo, backupData, callback) => {

  const _allowRestore = false
  const { appDataItemKey } = selectedInfo

  const batch = writeBatch(mainDb);

  _.forEach(backupData, (backupDataItem, key) => {
    const refPath = createRefPath_event(pathViews, [appDataItemKey, key]);
    database_fns.set_doc(refPath, backupDataItem, false, null, batch); // Assuming doc_update returns a promise
  });

  logBatch(batch)
  if (_allowRestore) {
    batch.commit()
  }

  callback()

}

const handleRemoveBadData = async (database_fns, selectedInfo, pathViews, callback) => {

  const { appDataItemKey, appData } = selectedInfo

  const _keys = []
  const _items = []

  _.forEach(appData, (data, attKey) => {
    if (!data.lastName) {
      _keys.push(attKey)
      _items.push(data)
    }
  })

  await database_fns.delete_collectionKeys(pathViews, _keys, appDataItemKey)
  callback()
}

const handleBackupAllDocuments = async (mainDb, database_fns, appDataDocuments, pathViews, callback) => {

  const _allowRestore = false
  const batch = writeBatch(mainDb);

  _.forEach(appDataDocuments, (appDataDocument, dataKey) => {
    const refPath = createRefPath_event(pathViews, ['_documents', dataKey], true);
    const docsToUpdate = {}
    _.forEach(appDataDocument, (itemData, itemKey) => {
      docsToUpdate[itemKey] = dataFix.createDeleteFields(itemData)
    })
    database_fns.set_doc(refPath, appDataDocument, false, null, batch); // Assuming doc_update returns a promise  
  })

  const refPath_main = createRefPath_event(pathViews, [], true);
  const data_main = { lastUpdate: getNow(), uid: appUser.uid, displayName: appUser.displayName };
  database_fns.set_doc(refPath_main, data_main, false, null, batch);

  logBatch(batch)
  if (_allowRestore) {
    await batch.commit()
  }

  callback()

}

const handleUpdateMultipleDocuments = async (
  mainDb,
  database_fns,
  collectionKeys,
  pathViews,
  callback
) => {

  for (const collectionKey of collectionKeys) {
    await handleUpdateDocuments(
      mainDb,
      database_fns,
      collectionKey,
      null,
      pathViews,
      () => { }
    )
  }

  if (callback) callback()
}

const handleUpdateDocuments = async (mainDb, database_fns, collectionKey, collectionData, pathViews, callback) => {

  const allow = true
  const batch = writeBatch(mainDb);

  const refPath = createRefPath_event(pathViews, ['_documents', collectionKey]);
  let _collectionData = collectionData

  if (!collectionData) {
    const refPathDoc = createRefPath_event(pathViews, [collectionKey]);
    _collectionData = await database_fns.get_data({ refPath: refPathDoc })
  }

  const docsToUpdate = {}

  _.forEach(_collectionData, (itemData, itemKey) => {
    docsToUpdate[itemKey] = dataFix.removeAllEmpties(itemData)
  })

  database_fns.set_doc(refPath, docsToUpdate, false, null, batch); // Assuming doc_update returns a promise   

  console.log('refPath', refPath, docsToUpdate)

  const _timestamp = Date.now()
  database_fns.update_timestamp(_cacheGroupTypes.eventInfo, pathViews.events, _timestamp, batch)

  // logBatch(batch)

  if (allow) {
    await batch.commit()
  }

  callback()

}

const handleUpdateClientProfiles = async (database_fns, pathViews, clientProfiles) => {

  try {
    // 1. Delete all existing profiles
    const refPath1 = createRefPath_client(pathViews, ["profiles"]);
    const existingProfiles = await database_fns.get_data({ refPath: refPath1 })

    // const existingProfiles = {
    //   aaaa: {
    //     email: 'aaaa'
    //   }
    // }

    const deletePromises = _.map(existingProfiles, (existingProfile) => {
      const { email } = existingProfile;
      const refPath = createRefPath_client(pathViews, ["profiles", email]);
      return database_fns.doc_delete(refPath, null, true);
    });

    await Promise.all(deletePromises);
    console.log("Cleared profiles collection");

    // 2. Re-add updated profiles
    const addPromises = _.map(clientProfiles, (clientProfile) => {
      const { email } = clientProfile;
      const refPath = createRefPath_client(pathViews, ["profiles", email]);
      return database_fns.doc_set(refPath, clientProfile);
    });

    await Promise.all(addPromises);

    console.log("Client Profiles completed successfully!");
    appDataSidebar_handlers.handleSet_currentConfirmationType();
    appDataSidebar_handlers.handleSet_updating(false);
  } catch (error) {
    console.error("Error during Profile Update", error);
    appDataSidebar_handlers.handleSet_currentConfirmationType();
    appDataSidebar_handlers.handleSet_updating(false);
  } finally {
    appDataSidebar_handlers.handleSet_updating(false);
  }
};


const handleBackups = async (mainDb, database_fns, appDataDocuments, pathViews, callback) => {

  const _allowRestore = false
  const batch = writeBatch(mainDb);

  _.forEach(appDataDocuments, (appDataDocument, dataKey) => {
    _.forEach(appDataDocument, (appDataItem, key) => {
      const refPath = createRefPath_event(pathViews, [dataKey, key], true);
      database_fns.set_doc(refPath, appDataItem, false, null, batch); // Assuming doc_update returns a promise
    });
  })

  const refPath_main = createRefPath_event(pathViews, [], true);
  const data_main = { lastUpdate: getNow(), uid: appUser.uid, displayName: appUser.displayName };
  database_fns.set_doc(refPath_main, data_main, false, null, batch)

  logBatch(batch)
  if (_allowRestore) {
    await batch.commit()
  }

  callback()

}

const handleBackup = async (mainDb, database_fns, pathViews, selectedInfo) => {

  const _allowRestore = false
  const batch = writeBatch(mainDb);

  const { appDataItemKey, appData } = selectedInfo

  _.forEach(appData, (appDataItem, key) => {
    const refPath = createRefPath_event(pathViews, [appDataItemKey, key], true);
    database_fns.set_doc(refPath, appDataItem, false, null, batch); // Assuming doc_update returns a promise
  });

  const refPath_main = createRefPath_event(pathViews, [], true);
  const data_main = { lastUpdate: getNow(), uid: appUser.uid, displayName: appUser.displayName };
  database_fns.set_doc(refPath_main, data_main, false, null, batch)

  logBatch(batch)
  if (_allowRestore) {
    await batch.commit()
  }

  callback()
}
